import { useCallback, useId, useRef, useState } from 'react';
import { Loader2, Pencil, RotateCw, Upload, X } from 'lucide-react';
import { uploadImage, type UploadImageOptions } from '@/lib/images/upload';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { ApiError } from '@/lib/api/errors';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { ImageEditor } from './ImageEditor';
import type { CompressPreset } from '@/lib/images/compress';

interface FileUploaderProps {
  value: string | null;
  onChange: (next: string | null) => void;
  label?: string;
  accept?: string;
  className?: string;
  /** External flag — parent should disable Save while uploading. */
  onUploadingChange?: (uploading: boolean) => void;
  /** Compression preset (default 'photo'; pass 'logo' or 'avatar' as needed). */
  preset?: CompressPreset;
  /** Allow the user to open the image editor before upload. Default true. */
  editable?: boolean;
  /** Aspect ratio passed to the editor (width / height). Default 1. */
  editorAspect?: number | null;
}

export function FileUploader({
  value,
  onChange,
  label,
  accept = 'image/png,image/jpeg,image/webp,image/svg+xml',
  className,
  onUploadingChange,
  preset = 'photo',
  editable = true,
  editorAspect = 1,
}: FileUploaderProps) {
  const t = useT();
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastFileRef = useRef<File | null>(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSrc, setEditorSrc] = useState<string | null>(null);

  const performUpload = useCallback(
    async (file: File) => {
      setError(null);
      setProgress(0);
      setUploading(true);
      onUploadingChange?.(true);
      const controller = new AbortController();
      abortRef.current = controller;
      lastFileRef.current = file;

      try {
        const opts: UploadImageOptions = {
          preset,
          onProgress: setProgress,
          signal: controller.signal,
        };
        const res = await uploadImage(file, opts);
        onChange(res.url);
      } catch (e) {
        const msg =
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : t('forms.uploader.uploadFailed');
        setError(msg);
      } finally {
        abortRef.current = null;
        setUploading(false);
        onUploadingChange?.(false);
      }
    },
    [onChange, onUploadingChange, preset, t],
  );

  const handlePick = (file: File) => {
    if (editable && file.type !== 'image/svg+xml') {
      // Open the editor with the source so the user can crop/rotate first.
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          setEditorSrc(reader.result);
          setEditorOpen(true);
        }
      };
      reader.readAsDataURL(file);
    } else {
      void performUpload(file);
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const handleRetry = () => {
    if (lastFileRef.current) void performUpload(lastFileRef.current);
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
          {label}
        </span>
      )}
      <label
        htmlFor={id}
        className={cn(
          'group relative flex h-32 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed bg-[rgb(0_0_0/0.3)] transition-colors',
          value
            ? 'border-[var(--color-accent)]'
            : 'border-[var(--color-border-default)] hover:border-[var(--color-border-strong)]',
        )}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handlePick(file);
            // Reset so the same file can be re-picked.
            e.target.value = '';
          }}
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2 px-4">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-fg-muted)]" />
            <div className="w-full max-w-[180px]">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[rgb(255_255_255/0.1)]">
                <div
                  className="h-full bg-[var(--color-accent)] transition-[width]"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-1 text-center text-[10px] text-[var(--color-fg-muted)]">
                {progress}%
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleCancel();
              }}
              className="text-[10px] uppercase tracking-wide text-[var(--color-fg-muted)] hover:text-[var(--color-error)]"
            >
              {t('forms.uploader.cancel')}
            </button>
          </div>
        ) : value ? (
          <>
            {/* crossOrigin must match what ImageEditor uses when re-fetching
                this URL via `new Image('anonymous')` — otherwise Chrome reuses
                the cached no-cors response and the editor's canvas becomes
                tainted (toBlob then throws SecurityError on save). */}
            <img
              src={resolveImageUrl(value)}
              alt=""
              crossOrigin="anonymous"
              className="max-h-full max-w-full object-contain"
            />
            <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              {editable && (
                <button
                  type="button"
                  aria-label={t('forms.uploader.edit')}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditorSrc(value);
                    setEditorOpen(true);
                  }}
                  className="rounded-md bg-[rgb(0_0_0/0.6)] p-1 text-white hover:bg-[var(--color-accent)]"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                aria-label={t('forms.uploader.remove')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(null);
                }}
                className="rounded-md bg-[rgb(0_0_0/0.6)] p-1 text-white hover:bg-[var(--color-error)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-6 w-6 text-[var(--color-fg-subtle)]" />
            <span className="text-xs text-[var(--color-fg-muted)]">
              {t('forms.uploader.formats')}
            </span>
          </div>
        )}
      </label>

      {error && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 px-2 py-1.5 text-xs text-[var(--color-error)]">
          <span className="truncate">{error}</span>
          {lastFileRef.current && (
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex shrink-0 items-center gap-1 font-semibold hover:underline"
            >
              <RotateCw className="h-3 w-3" />
              {t('forms.uploader.retry')}
            </button>
          )}
        </div>
      )}

      <ImageEditor
        open={editorOpen}
        src={editorSrc}
        aspect={editorAspect}
        onClose={() => setEditorOpen(false)}
        onSave={(file) => {
          setEditorOpen(false);
          void performUpload(file);
        }}
      />
    </div>
  );
}
